import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, BookOpen, Users, Sparkles, Star } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const JANELLE_PHOTO = 'https://customer-assets.emergentagent.com/job_98f4c0a7-37e3-4eee-b45d-3c901b2920ec/artifacts/e2kpke7l_IMG_5756.jpeg';

export const HomePage = () => {
  return (
    <div data-testid="home-page" className="min-h-screen bg-cream">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-sage/5 to-cream">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Faith-Led Coaching</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-charcoal mb-5 leading-tight">
                Purpose Reclaimed Coaching
              </h1>
              <p className="text-base md:text-lg text-charcoal-light mb-8 leading-relaxed">
                Helping women heal from trauma, abuse, and limiting beliefs through faith-led coaching. 
                Let go of guilt, shame, and blame. Step into the empowered, purposeful life God created for you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/coaching" data-testid="hero-coaching-btn"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all shadow-lg hover:shadow-xl">
                  Work With Janelle <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/purchase" data-testid="hero-ebook-btn"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-sage border border-sage/20 rounded-full font-medium hover:bg-sage/5 transition-all">
                  Get the Ebook
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src={JANELLE_PHOTO}
                alt="Janelle McGowan-Charles"
                data-testid="hero-photo"
                className="w-72 h-72 md:w-80 md:h-80 object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl text-charcoal text-center mb-4">How I Can Help You</h2>
          <p className="text-charcoal-light text-center mb-12 max-w-2xl mx-auto">
            Whether you're ready for 1-to-1 coaching, want to start with the ebook, or are looking for a community workshop — there's a path for you.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Coaching */}
            <div data-testid="service-coaching" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all text-center">
              <div className="w-16 h-16 mx-auto mb-5 rounded-xl bg-sage/10 flex items-center justify-center">
                <Heart className="w-8 h-8 text-sage" />
              </div>
              <h3 className="font-heading text-xl text-charcoal mb-3">1-to-1 Coaching</h3>
              <p className="text-sm text-charcoal-light mb-5">
                Personalised faith-led coaching to help you break through trauma, set boundaries, and reclaim your identity. Free 1-hour clarity session.
              </p>
              <Link to="/coaching" className="text-sage font-medium text-sm hover:underline inline-flex items-center gap-1">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Ebook */}
            <div data-testid="service-ebook" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all text-center">
              <div className="w-16 h-16 mx-auto mb-5 rounded-xl bg-gold/10 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-gold" />
              </div>
              <h3 className="font-heading text-xl text-charcoal mb-3">Breakthrough Ebook</h3>
              <p className="text-sm text-charcoal-light mb-5">
                A 6-chapter healing guide with journal prompts, prayers, worksheets, and bonus resources. Start from just £17.
              </p>
              <Link to="/purchase" className="text-gold font-medium text-sm hover:underline inline-flex items-center gap-1">
                View Packages <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Workshops */}
            <div data-testid="service-workshops" className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all text-center">
              <div className="w-16 h-16 mx-auto mb-5 rounded-xl bg-sky/10 flex items-center justify-center">
                <Users className="w-8 h-8 text-sky-dark" />
              </div>
              <h3 className="font-heading text-xl text-charcoal mb-3">Workshops</h3>
              <p className="text-sm text-charcoal-light mb-5">
                Group workshops for women overcoming abuse and trauma. Heal in community. Coming soon — join the waitlist.
              </p>
              <Link to="/workshops" className="text-sky-dark font-medium text-sm hover:underline inline-flex items-center gap-1">
                Join Waitlist <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Janelle Teaser */}
      <section className="bg-sage/5 py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-charcoal mb-6">Meet Janelle</h2>
          <p className="text-charcoal-light leading-relaxed mb-8 max-w-2xl mx-auto">
            I'm Janelle McGowan-Charles — a faith-led trauma recovery coach and the founder of Purpose Reclaimed Coaching. 
            I help women who have experienced sexual abuse, trauma, and deep emotional wounds to heal, forgive, and step 
            into the powerful, purposeful life God has for them. My own journey through pain and healing is what fuels 
            everything I do.
          </p>
          <Link to="/about" className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all">
            Read My Story <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Testimonials Placeholder */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl text-charcoal text-center mb-4">What Women Are Saying</h2>
          <p className="text-charcoal-light text-center mb-12">Real stories of transformation</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { text: "Working with Janelle changed my life. For the first time, I feel free from the shame I carried for years.", name: "Client Testimonial", tag: "Coaching Client" },
              { text: "The Breakthrough ebook spoke directly to my heart. The prayers and journal prompts helped me process things I'd buried for decades.", name: "Client Testimonial", tag: "Ebook Reader" },
              { text: "I finally understand that what happened to me doesn't define me. Janelle helped me see myself through God's eyes.", name: "Client Testimonial", tag: "Coaching Client" }
            ].map((t, i) => (
              <div key={i} data-testid={`testimonial-${i+1}`} className="bg-white rounded-2xl p-6 shadow-sm border border-stone-100">
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-gold text-gold" />)}
                </div>
                <p className="text-sm text-charcoal-light italic mb-4 leading-relaxed">"{t.text}"</p>
                <div>
                  <p className="text-sm font-medium text-charcoal">{t.name}</p>
                  <p className="text-xs text-charcoal-light">{t.tag}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-sage/5 py-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-charcoal mb-4">Ready to Begin Your Breakthrough?</h2>
          <p className="text-charcoal-light mb-8">Your first coaching session is completely free. No pressure, no obligation — just a safe space to begin.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all shadow-lg">
            Book Your Free Session <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};
